import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, first, Subject, takeUntil } from 'rxjs';
import { LgvAPIService, LgvClientService, LgvConstants, LgvUserService, NotificationService } from '../../core/core.module';
import { LgvModel } from '../../data-models/longevity.model';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { LgvAuthMgrService } from '../../core/services/lgv-auth-mgr.service';

@Component({
    selector: 'lgv-edit-client-profile',
    templateUrl: './edit-client-profile.component.html',
    styleUrls: ['./edit-client-profile.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditClientProfileComponent implements OnInit {


    pageModeParam: string;
    needRefreshParam: false;

    _pageMode = 'Profile';
    pageMode$ = new BehaviorSubject(this._pageMode);

    hasEditBeenMade = false;

    _clientInfo: LgvModel.ClientInfo = null;
    clientInfo$ = new BehaviorSubject(this._clientInfo);

    isLoading = false;
    isLoading$ = new BehaviorSubject(this.isLoading);
    onDestroy$ = new Subject();

    contactInfoForm: UntypedFormGroup;
    financialDetailsForm: UntypedFormGroup;

    get readyForDTI(): boolean {
        return this.isProfileMode ? !!(this.clientInfo$.value.totalDebtPayments) && !!(this.clientInfo$.value.totalIncome) && this.clientInfo$.value.totalDebtPayments > 0 && this.clientInfo$.value.totalIncome > 0 :
        !!(this.financialDetailsForm.controls.totalDebtPayments.value) && !!(this.financialDetailsForm.controls.totalIncome.value) && this.financialDetailsForm.controls.totalDebtPayments.value > 0 && this.financialDetailsForm.controls.totalIncome.value > 0
    }

    get DTI_EditMode(): string { return this.readyForDTI ? ((this.financialDetailsForm.controls.totalDebtPayments.value / this.financialDetailsForm.controls.totalIncome.value) * 100).toFixed(2) : null }
    get DTI_ViewMode(): string { return this.readyForDTI ? ((this.clientInfo$.value?.totalDebtPayments / this.clientInfo$.value.totalIncome) * 100).toFixed(2) : null }
    get DTI(): string { return this.isProfileMode ? this.DTI_ViewMode : this.DTI_EditMode }

    get isProfileMode(): boolean { return this.pageMode$.value === 'Profile'; }
    get isEditContactInformationMode(): boolean { return this.pageMode$.value === 'EditContactInformation'; }
    get isEditFinancialDetailsMode(): boolean { return this.pageMode$.value === 'EditFinancialDetails'; }

    constructor(
        private fb: UntypedFormBuilder,
        public dialogRef: MatDialogRef<EditClientProfileComponent>,
        public MatDialog: MatDialog,
        private authMgr: LgvAuthMgrService,
        public lgvClientService: LgvClientService,
        public notificationService: NotificationService,
        public lgvApiService: LgvAPIService,
        public lgvUserService: LgvUserService,

        @Inject(MAT_DIALOG_DATA) public data: any) {

        if (data.clientInfo) {
            this.clientInfo$.next(data.clientInfo);
        }

        if (data.needRefresh) {
            this.needRefreshParam = data.needRefresh;
        }

        if (data.pageMode) {
            this.pageModeParam = data.pageMode;

            switch (this.pageModeParam) {
                case 'EditContactInformation':
                    this.onEditContactInformation();
                    break;
                case 'EditFinancialDetails':
                    this.onEditFinancialDetails();
                    break;
            }
        }

    }

    ngOnInit(): void {
        if (!this.clientInfo$.value) {
            this.fetchLatestContactInfo();
        }
    }


    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    fetchLatestContactInfo() {
        this.isLoading$.next(true);

        if (this.authMgr.IsClient) {
            this.lgvClientService.ClientInfo$.pipe(first(f => f !== null), takeUntil(this.onDestroy$)).subscribe(x => {
                this.clientInfo$.next(x);
                this.isLoading$.next(false);
            });
        }
        else {
            let clientID = this.clientInfo$.value?.id;

            if (clientID) {
                this.lgvClientService.FetchClientInfo(clientID).pipe(first(f => f !== null), takeUntil(this.onDestroy$)).subscribe(x => {
                    this.clientInfo$.next(x);
                    this.isLoading$.next(false);
                });

            }
        }


    }

    initContactInfoForm() {

        let ci = this.clientInfo$.value;

        this.contactInfoForm = this.fb.group({
            ownerId: [ci.ownerId],
            firstName: [ci.firstName, [Validators.required]],
            lastName: [ci.lastName, [Validators.required]],
            email: [ci.email, [Validators.required, Validators.email]],
            mobilePhone: [ci.mobilePhone]
        });
    }

    initFinancialDetailsForm() {

        let ci = this.clientInfo$.value;

        this.financialDetailsForm = this.fb.group({
            ownerId: [ci.ownerId],
            fico: [ci.fico, [Validators.required]],
            totalIncome: [ci.totalIncome, [Validators.required]],
            totalDebtPayments: [ci.totalDebtPayments, [Validators.required]],
            firstTimeBuyer: [ci.firstTimeBuyer, [Validators.required]]
        });

    }

    onEditContactInformation() {
        if (!this.clientInfo$.value) {
            this.notificationService.error('Client Information not set up');
            return;
        }
        this.initContactInfoForm();
        this.pageMode$.next('EditContactInformation');
    }

    onEditFinancialDetails() {
        console.log('onEditFinancialDetails()');
        console.log(this.clientInfo$.value);

        if (!this.clientInfo$.value) {
            this.notificationService.error('Client Information not set up');
            return;
        }
        this.initFinancialDetailsForm();
        this.pageMode$.next('EditFinancialDetails');
    }

    onClose(): void {
        this.dialogRef.close({ action: this.hasEditBeenMade ? true : false });
    }

    onCancelEdit() {
        if (!this.pageModeParam) {
            this.pageMode$.next('Profile');
        }
        else {
            this.dialogRef.close({ action: false });
        }
    }

    onFinishedEditing() {
        if (!this.pageModeParam) {
            this.pageMode$.next('Profile');

            if (this.authMgr.IsClient) {
                this.lgvClientService.LoadSnapshotForClient(true);
            }
            this.fetchLatestContactInfo();
        }
        else {
            // Returning true here will cause the parent page to do another refresh.  Doesn't appear to be needed.
            this.dialogRef.close({ action: !!this.needRefreshParam ? true : false });
        }
    }

    OnConfirmSave() {

        if (this.isEditContactInformationMode && this.contactInfoForm.invalid) {
            return
        }

        if (this.isEditFinancialDetailsMode && this.financialDetailsForm.invalid) {
            return
        }

        if (this.isEditFinancialDetailsMode) {
            const dialogRef = this.MatDialog.open(ConfirmationDialogComponent, {
                width: '400px',
                data: { Confirmation: LgvConstants.ClientProfileWarning }
            });

            dialogRef.afterClosed().subscribe(x => {
                if (x.action == true) {
                    this.proceedWithClientSave();
                }
            });
        }
        else {
            this.proceedWithClientSave();
        }

    }

    proceedWithClientSave() {
        let form = this.isEditContactInformationMode ? this.contactInfoForm : this.financialDetailsForm;

        let model = form.getRawValue();

        if (this.isEditFinancialDetailsMode) {
            // Temporary using "monthlyDebt" in the payload instead of "totalDebtPayments"
            model.monthlyDebt = model.totalDebtPayments;
            delete model.totalDebtPayments;
        }

        this.isLoading$.next(true);
        this.lgvClientService.SaveClient(model, this.clientInfo$.value.id).pipe(first()).subscribe(clientResponse => {
            if (clientResponse) {
                this.notificationService.success('Profile has been saved');
                this.isLoading$.next(false);
                this.hasEditBeenMade = true;
                this.onFinishedEditing();
            }
        }, (err) => {
            this.isLoading$.next(false);
            this.notificationService.error('There was an error updating the profile');
            console.log(err);
        })
    }


}
